import { apiFetch } from './ApiService.js';
import type { IToastService } from './ToastService.js';
import type { IModalService } from './ModalService.js';
import type { IModalConfirmHelper } from './ModalConfirmHelper.js';

/// Håndterer write-actions for mail-brugere (`data-action="create-user"`).
/// Læser felter fra modal-DOM, kalder Admin-app's `/api/users`-broker som
/// forwarder til MailApi /admin/users med bearer-auth.
///
/// `<form method="post">` er forbudt på Admin-siden — alle writes går
/// gennem JS fetch.

interface CreateUserPayload {
  localPart: string;
  domain: string;
  autoEncryptAtRest: boolean;
}

interface BrokerResponse {
  ok: boolean;
  detail?: string | null;
}

export interface IUserActionsService {
  createFromModal(modalId: string): Promise<void>;
  disable(email: string): Promise<void>;
  restore(email: string): Promise<void>;
  deleteWithConfirm(email: string): void;
}

export class UserActionsService implements IUserActionsService {
  constructor(
    private readonly toast: IToastService,
    private readonly modal: IModalService,
    private readonly confirmer: IModalConfirmHelper,
    private readonly doc: Document = document,
  ) { }

  /// Hard-delete af bruger med GitHub-style confirm-modal. Bruger skal skrive
  /// email for at aktivere primær-knap. På success → redirect til /Users
  /// (vi er typisk på /Users/Detail og den side ophører med at eksistere).
  deleteWithConfirm(email: string): void {
    this.confirmer.open({
      title: 'Slet bruger endeligt',
      message: `Sletter brugeren ${email} + alle mails, mailboxes og credentials permanent. Kan ikke fortrydes.`,
      expectedName: email,
      actionUrl: `/api/users/${encodeURIComponent(email)}/delete`,
      successMsg: `Bruger slettet: ${email}`,
      redirectTo: '/Users',
    });
  }

  async createFromModal(modalId: string): Promise<void> {
    const modal = this.doc.getElementById(modalId);
    if (!modal) {
      console.warn(`UserActionsService: modal '${modalId}' findes ikke`);
      return;
    }

    const payload = this.readPayload(modal);
    if (!this.validate(modal, payload)) return;

    try {
      const resp = await apiFetch('/api/users', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = await this.readBody(resp);
      if (resp.ok && body.ok) {
        this.toast.show(`Bruger oprettet: ${payload.localPart}@${payload.domain}`, 'success');
        this.modal.close(modalId);
        // Multi-admin policy: refresh er OK; siden re-renderes via OnGet.
        window.location.reload();
        return;
      }

      const msg = body.detail ?? `HTTP ${resp.status}`;
      this.toast.show(`Kunne ikke oprette bruger: ${msg}`, 'error');
    } catch (e) {
      console.error(e);
      this.toast.show('Netværksfejl — kunne ikke nå Admin-server', 'error');
    }
  }

  /// Disable konto med browser-confirm. Microsoft-account-pattern: 90 dages
  /// grace; brugeren kan stadig læse via JMAP men kan ikke sende/modtage.
  async disable(email: string): Promise<void> {
    const ok = window.confirm(
      `Vil du virkelig disable ${email}?\n\n` +
      `Brugeren vil ikke kunne sende eller modtage mail, ` +
      `men kan stadig læse eksisterende mails via klienten. ` +
      `Indholdet slettes permanent efter 90 dage hvis ikke restored.`);
    if (!ok) return;
    await this.postSimple(`/api/users/${encodeURIComponent(email)}/disable`,
      `Konto disabled: ${email}`, 'Kunne ikke disable konto');
  }

  async restore(email: string): Promise<void> {
    await this.postSimple(`/api/users/${encodeURIComponent(email)}/restore`,
      `Konto genaktiveret: ${email}`, 'Kunne ikke genaktivere konto');
  }

  private async postSimple(url: string, successMsg: string, errPrefix: string): Promise<void> {
    try {
      const resp = await apiFetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });
      const body = await this.readBody(resp);
      if (resp.ok && body.ok) {
        this.toast.show(successMsg, 'success');
        window.location.reload();
        return;
      }
      const msg = body.detail ?? `HTTP ${resp.status}`;
      this.toast.show(`${errPrefix}: ${msg}`, 'error');
    } catch (e) {
      console.error(e);
      this.toast.show('Netværksfejl — kunne ikke nå Admin-server', 'error');
    }
  }

  private readPayload(modal: HTMLElement): CreateUserPayload {
    return {
      localPart: this.readInput(modal, 'localPart'),
      domain: this.readInput(modal, 'domain'),
      autoEncryptAtRest: this.readCheckbox(modal, 'autoEncryptAtRest'),
    };
  }

  private readInput(scope: HTMLElement, name: string): string {
    const el = scope.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);
    return (el?.value ?? '').trim();
  }

  private readCheckbox(scope: HTMLElement, name: string): boolean {
    const el = scope.querySelector<HTMLInputElement>(`[name="${name}"]`);
    return el?.checked ?? false;
  }

  /// Klient-side validering matcher hvad MailApi accepterer. Vi fanger her
  /// så brugeren får øjeblikkelig feedback i stedet for at vente på 4xx.
  private validate(modal: HTMLElement, p: CreateUserPayload): boolean {
    this.clearErrors(modal);
    let ok = true;

    if (!p.localPart) {
      this.showFieldError(modal, 'localPart', 'Lokal-del er påkrævet');
      ok = false;
    } else if (!/^[a-zA-Z0-9._-]+$/.test(p.localPart)) {
      this.showFieldError(modal, 'localPart', 'Kun bogstaver, tal, punktum, bindestreg og underscore');
      ok = false;
    }

    if (!p.domain) {
      this.showFieldError(modal, 'domain', 'Vælg et domæne');
      ok = false;
    }

    return ok;
  }

  private clearErrors(modal: HTMLElement): void {
    modal.querySelectorAll<HTMLElement>('[data-field-error]').forEach(el => {
      el.textContent = '';
    });
  }

  private showFieldError(modal: HTMLElement, field: string, msg: string): void {
    const el = modal.querySelector<HTMLElement>(`[data-field-error="${field}"]`);
    if (el) el.textContent = msg;
  }

  private async readBody(resp: Response): Promise<BrokerResponse> {
    try {
      return await resp.json() as BrokerResponse;
    } catch {
      return { ok: false, detail: `HTTP ${resp.status}` };
    }
  }
}
